import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import type { ProjectoCampaign, ProjectoPLSummary } from '../../types/projecto';
import { formatEuro } from '../../utils/currency';
import { FormBlock } from '../ui/FormBlock';

export type BillingTerms = Pick<ProjectoCampaign, 'budget_enabled' | 'budget_amount' | 'target_margin_pct' | 'default_commission_pct'>;

const MODES = [
  {
    fixedBudget: false,
    title: 'Custos + comissão',
    text: 'Vamos somando os custos, juntamos a comissão e cobramos o total ao cliente.',
  },
  {
    fixedBudget: true,
    title: 'Orçamento fechado',
    text: 'O cliente paga um valor fixo. O que sobra depois dos custos é a margem.',
  },
];

/** A number input that only reports when you leave it, so it saves once. */
function TermInput({ label, value, suffix, help, onCommit, invalid }: {
  label: string;
  value: number;
  suffix: string;
  help?: string;
  invalid?: boolean;
  onCommit: (value: number) => void;
}) {
  const [text, setText] = useState(value ? String(value) : '');
  useEffect(() => { setText(value ? String(value) : ''); }, [value]);

  const commit = () => {
    const next = Math.max(0, parseFloat(text.replace(',', '.')) || 0);
    if (next !== value) onCommit(next);
  };

  return (
    <label className="cm-field block">
      <span className="cm-label">{label}</span>
      <span className="cm-affix has-right">
        <input
          type="text"
          inputMode="decimal"
          value={text}
          placeholder="0"
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          className={`cm-input cm-numeric ${invalid ? '!border-amber-400' : ''}`}
        />
        <span>{suffix}</span>
      </span>
      {help && <span className="cm-help block">{help}</span>}
    </label>
  );
}

function BudgetMeter({ budget, costs, targetMarginPct }: { budget: number; costs: number; targetMarginPct: number }) {
  if (budget <= 0) return null;
  const spentPct = Math.min(100, (costs / budget) * 100);
  const limit = targetMarginPct > 0 ? budget * (1 - targetMarginPct / 100) : budget;
  const left = budget - costs;
  const bar = costs > budget ? 'bg-red-500' : costs > limit ? 'bg-amber-400' : 'bg-emerald-500';

  return (
    <div className="md:col-span-3 space-y-1.5" data-testid="budget-meter">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 text-xs">
        <span className="text-gray-600">
          Gasto <strong className="text-gray-900 tabular-nums">{formatEuro(costs)}</strong> de {formatEuro(budget)}
        </span>
        <span className={left < 0 ? 'text-red-700 font-semibold' : 'text-gray-600'}>
          {left < 0 ? `${formatEuro(-left)} acima do orçamento` : `${formatEuro(left)} disponíveis`}
        </span>
      </div>
      <div className="relative h-2 rounded-full bg-gray-100">
        <div className={`h-full rounded-full transition-all ${bar}`} style={{ width: `${spentPct}%` }} />
        {targetMarginPct > 0 && targetMarginPct < 100 && (
          <span aria-hidden className="absolute -top-0.5 -bottom-0.5 w-0.5 bg-brand-ink" style={{ left: `${100 - targetMarginPct}%` }} />
        )}
      </div>
      {targetMarginPct > 0 && (
        <p className="text-xs text-gray-500">
          {costs <= limit
            ? `Para manter ${targetMarginPct}% de margem podes gastar até ${formatEuro(limit)}: faltam ${formatEuro(limit - costs)}.`
            : `Já passaste os ${formatEuro(limit)} que mantinham ${targetMarginPct}% de margem.`}
        </p>
      )}
    </div>
  );
}

/** How the client is charged: this decides what the rest of the P&L shows. */
export function ProjectoBillingBlock({ terms, summary, linesOffCommission, onChange, onApplyCommission }: {
  terms: BillingTerms;
  summary: ProjectoPLSummary;
  /** Supplier lines whose commission is not the project's. */
  linesOffCommission: number;
  onChange: (updates: Partial<BillingTerms>) => void;
  onApplyCommission: () => void;
}) {
  const fixedBudget = terms.budget_enabled;

  return (
    <FormBlock title="Como cobramos ao cliente">
      <div role="radiogroup" aria-label="Como cobramos ao cliente" className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {MODES.map((mode) => {
          const active = mode.fixedBudget === fixedBudget;
          return (
            <button
              key={mode.title}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => { if (!active) onChange({ budget_enabled: mode.fixedBudget }); }}
              className={`flex items-start gap-3 rounded-xl border-[1.5px] p-3 text-left transition-colors ${
                active ? 'border-brand-ink bg-brand-tint' : 'border-gray-200 hover:border-gray-400'
              }`}
            >
              <span className={`mt-0.5 grid place-items-center w-5 h-5 shrink-0 rounded-full border-[1.5px] ${active ? 'border-brand-ink bg-brand-ink text-brand-lime' : 'border-gray-300'}`}>
                {active && <Check className="w-3 h-3" strokeWidth={3} />}
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-gray-900">{mode.title}</span>
                <span className="block text-xs text-gray-500 mt-0.5">{mode.text}</span>
              </span>
            </button>
          );
        })}
      </div>

      {fixedBudget ? (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 md:items-start">
          <TermInput
            label="Orçamento do cliente"
            suffix="€"
            help="Sem IVA."
            value={terms.budget_amount}
            invalid={terms.budget_amount <= 0}
            onCommit={(budget_amount) => onChange({ budget_amount })}
          />
          <TermInput
            label="Margem que queremos"
            suffix="%"
            help="Opcional. Avisa quando os custos a põem em causa."
            value={terms.target_margin_pct}
            onCommit={(target_margin_pct) => onChange({ target_margin_pct: Math.min(100, target_margin_pct) })}
          />
          <BudgetMeter budget={terms.budget_amount} costs={summary.costs} targetMarginPct={terms.target_margin_pct} />
        </div>
      ) : (
        <div className="mt-4 flex flex-wrap items-start gap-x-6 gap-y-3">
          <div className="w-full sm:w-56">
            <TermInput
              label="Comissão do projeto"
              suffix="%"
              help="Aplicada a cada nova linha."
              value={terms.default_commission_pct}
              onCommit={(default_commission_pct) => onChange({ default_commission_pct })}
            />
          </div>
          <div className="sm:pt-7 flex flex-wrap items-center gap-x-4 gap-y-2">
            {terms.default_commission_pct > 0 && linesOffCommission > 0 && (
              <button type="button" onClick={onApplyCommission} className="cm-btn-add">
                Aplicar {terms.default_commission_pct}% a {linesOffCommission === 1 ? '1 linha' : `${linesOffCommission} linhas`}
              </button>
            )}
            {summary.commission !== 0 && (
              <span className="text-xs text-gray-600">
                Comissão atual: <strong className="text-gray-900 tabular-nums">{formatEuro(summary.commission)}</strong>
                {' '}({summary.commissionPct.toLocaleString('pt-PT', { maximumFractionDigits: 1 })}% sobre os custos)
              </span>
            )}
          </div>
        </div>
      )}
    </FormBlock>
  );
}
